import { audioSourceKey, editDurationMs, isStill, sourceKey } from './edit.js';
import { gainAt, mixPlan } from './edit-mix.js';
/** The effects the playout reproduces (black bars); the others are drawn only by the editor. */
const AIR_EFFECTS = new Set(['bars']);
/** What in an edit the playout's render cannot reproduce faithfully. Empty: it can render it. */
export function airBlockers(p) {
    const out = [];
    if (p.format.aspect !== '16:9')
        out.push({ piece: null, code: 'aspect' });
    if (p.lanes.hidden.includes('video'))
        out.push({ piece: null, code: 'video-hidden' });
    p.segments.forEach((s, i) => {
        const l = s.look;
        if ((l.filter !== 'none' && l.amount > 0) || l.brightness || l.contrast || l.saturation || l.temperature || l.vignette || l.blur)
            out.push({ piece: i, code: 'look' });
        if (s.motion.type !== 'none')
            out.push({ piece: i, code: 'motion' });
        for (const e of s.effects)
            if (e.amount > 0 && !AIR_EFFECTS.has(e.type))
                out.push({ piece: i, code: 'effect', detail: e.type });
        if (Object.values(s.keys ?? {}).some((k) => (k?.length ?? 0) > 0))
            out.push({ piece: i, code: 'keyframes' });
    });
    return out;
}
/** Whether the edit has anything drawn over the pictures (the graphics pack). */
export function airHasGraphics(p) {
    const hidden = new Set(p.lanes.hidden);
    return (!hidden.has('overlay') && p.overlays.length > 0) || (!hidden.has('text') && p.texts.some((t) => t.text.trim())) || (!hidden.has('straps') && p.straps.some((s) => s.lines.some((l) => l.trim())));
}
/** The name of the graphics pack among a job's files. */
export const AIR_GRAPHICS_FILE = 'graphics.pack';
/**
 * A voice's final level along the sequence, in milliseconds: its own points × its track's level × (for the music)
 * the drop under the voice-over and in the dips × the end fade. The product of ramps is not a ramp: a point is added
 * halfway between corners, which keeps the error far below anything heard.
 */
export function airVoicePoints(v, track, music, master) {
    const from = v.atS;
    const to = v.atS + v.durS;
    const corners = [...new Set([from, to, ...[...v.points, ...(music ?? []), ...master].map((x) => x.atS).filter((x) => x > from && x < to)])].sort((a, b) => a - b);
    const times = [];
    corners.forEach((t, k) => {
        times.push(t);
        const next = corners[k + 1];
        if (next !== undefined && next - t > 0.01)
            times.push((t + next) / 2);
    });
    const level = (t) => gainAt(v.points, t) * track * (music ? gainAt(music, t) : 1) * gainAt(master, t);
    const pts = times.map((t) => ({ atMs: Math.round(t * 1000), gain: Math.round(level(t) * 10_000) / 10_000 }));
    // straight runs need only their ends
    return pts.filter((p, k) => {
        const a = pts[k - 1];
        const b = pts[k + 1];
        if (!a || !b || b.atMs === a.atMs)
            return true;
        const lin = a.gain + ((b.gain - a.gain) * (p.atMs - a.atMs)) / (b.atMs - a.atMs);
        return Math.abs(lin - p.gain) > 0.0005;
    });
}
/** The render list of an edit (call `airBlockers` first: this reproduces only what the playout can). */
export function airRenderList(p, o) {
    const files = new Map();
    const fileSource = (key) => {
        const name = o.fileOf(key);
        files.set(name, key);
        return { file: name };
    };
    const video = (s) => (s.kind === 'clip' ? { media: s.id } : fileSource(sourceKey(s)));
    const audio = (s) => (s.kind === 'clip' ? { media: s.id } : fileSource(audioSourceKey(s)));
    const pieces = p.segments.map((s) => ({
        source: video(s.source),
        mode: s.source.kind === 'image' ? 'still' : s.freeze ? 'freeze' : 'video',
        inMs: s.inMs,
        outMs: s.outMs,
        speed: isStill(s) ? 1 : s.speed,
        transitionIn: s.transitionIn ? { type: s.transitionIn.type, durationMs: s.transitionIn.durationMs } : null,
        transform: { scale: s.transform.scale, x: s.transform.x, y: s.transform.y, rotation: s.transform.rotation, flipH: s.transform.flipH, flipV: s.transform.flipV, opacity: s.transform.opacity, crop: { ...s.transform.crop } },
        bars: s.effects.find((e) => e.type === 'bars')?.amount ?? 0,
    }));
    const plan = mixPlan(p, o.durations);
    const sourceOfKey = new Map();
    for (const s of p.segments)
        if (s.source.kind !== 'image')
            sourceOfKey.set(sourceKey(s.source), { kind: s.source.kind, id: s.source.id });
    for (const c of p.audio)
        sourceOfKey.set(audioSourceKey(c.source), c.source);
    const voices = [];
    for (const v of plan.voices) {
        const level = plan.tracks[v.track];
        const src = sourceOfKey.get(v.key);
        if (!src || level <= 0)
            continue;
        const points = airVoicePoints(v, level, v.track === 'music' ? plan.musicEnvelope : null, plan.master);
        // a voice that is silent all along is not sent
        if (points.every((x) => x.gain <= 0))
            continue;
        const atMs = Math.max(0, Math.round(v.atS * 1000));
        voices.push({ source: audio(src), atMs, offsetMs: Math.max(0, Math.round(v.offsetS * 1000)), durMs: Math.max(1, Math.round(v.durS * 1000)), rate: v.rate, points });
    }
    const spec = {
        version: 1,
        libraryId: o.libraryId,
        title: o.title,
        filename: o.filename,
        durationMs: editDurationMs(p),
        fit: p.format.fit,
        background: p.format.background === 'blur' ? { kind: 'blur' } : { kind: 'color', color: p.format.background === 'color' ? p.format.color : '#000000' },
        pieces,
        endFade: p.endFade ? { color: p.endFade.color, durationMs: p.endFade.durationMs } : null,
        graphics: airHasGraphics(p) ? AIR_GRAPHICS_FILE : null,
        voices,
        ...(o.origin ? { origin: o.origin } : {}),
    };
    if (spec.graphics)
        files.set(AIR_GRAPHICS_FILE, 'graphics');
    return { spec, files };
}
/** Write a pack from its pictures (PNG bytes) and the frames that show them. Neighbouring frames of one picture are merged. */
export function writeGraphicsPack(head, blobs, frames) {
    const merged = [];
    for (const f of frames) {
        const last = merged[merged.length - 1];
        if (last && last.blob === f.blob && last.toMs === f.fromMs)
            last.toMs = f.toMs;
        else
            merged.push({ ...f });
    }
    let off = 0;
    const manifest = { version: 1, ...head, blobs: blobs.map((b) => ({ offset: (off += b.length) - b.length, length: b.length })), frames: merged };
    const json = new TextEncoder().encode(JSON.stringify(manifest));
    const out = new Uint8Array(8 + json.length + off);
    out.set([0x41, 0x50, 0x47, 0x50], 0); // "APGP"
    new DataView(out.buffer).setUint32(4, json.length, false);
    out.set(json, 8);
    let at = 8 + json.length;
    for (const b of blobs) {
        out.set(b, at);
        at += b.length;
    }
    return out;
}
/** Read a pack's manifest (and check its frame cover), as the playout will. */
export function readGraphicsPackManifest(pack) {
    if (pack.length < 8 || String.fromCharCode(...pack.subarray(0, 4)) !== 'APGP')
        throw new Error('not a graphics pack');
    const len = new DataView(pack.buffer, pack.byteOffset, pack.byteLength).getUint32(4, false);
    const m = JSON.parse(new TextDecoder().decode(pack.subarray(8, 8 + len)));
    let t = 0;
    for (const f of m.frames) {
        if (f.fromMs !== t || f.toMs <= f.fromMs || f.blob < 0 || f.blob >= m.blobs.length)
            throw new Error('graphics pack: frames out of order');
        t = f.toMs;
    }
    if (t !== m.durationMs)
        throw new Error('graphics pack: frames do not cover it');
    return m;
}
//# sourceMappingURL=edit-air.js.map